import React, { useState } from 'react';

const BusinessHours = ({ hours, onChange }) => {
  const [businessHours, setBusinessHours] = useState(() => {
    // Default structure
    const defaultHours = {
      Monday: { isOpen: true, start: '11:00 AM', end: '8:00 PM' },
      Tuesday: { isOpen: true, start: '11:00 AM', end: '8:00 PM' },
      Wednesday: { isOpen: true, start: '11:00 AM', end: '8:00 PM' },
      Thursday: { isOpen: true, start: '11:00 AM', end: '8:00 PM' },
      Friday: { isOpen: true, start: '11:00 AM', end: '8:00 PM' },
      Saturday: { isOpen: true, start: '11:00 AM', end: '8:00 PM' },
      Sunday: { isOpen: false, start: '11:00 AM', end: '8:00 PM' }
    };

    // Parse existing hours if any
    if (hours && hours.length > 0) {
      const parsedHours = { ...defaultHours };
      hours.forEach(hourStr => {
        if (typeof hourStr === 'string') {
          const [dayPart, timePart] = hourStr.split(':').map(s => s.trim());
          if (dayPart && parsedHours[dayPart]) {
            if (timePart === 'Closed') {
              parsedHours[dayPart] = { ...parsedHours[dayPart], isOpen: false };
            } else {
              const [start, end] = timePart.split('-').map(t => t.trim());
              if (start && end) {
                parsedHours[dayPart] = { isOpen: true, start, end };
              }
            }
          }
        }
      });
      return parsedHours;
    }
    return defaultHours;
  });

  // Generate time options in 30-minute intervals
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of ['00', '30']) {
        const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour >= 12 ? 'PM' : 'AM';
        options.push(`${h}:${minute} ${period}`);
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const handleHourChange = (day, field, value) => {
    const updatedHours = {
      ...businessHours,
      [day]: { ...businessHours[day], [field]: value }
    };
    setBusinessHours(updatedHours);
    
    // Format hours for parent component
    const formattedHours = Object.entries(updatedHours)
      .filter(([day]) => !day.includes('-'))
      .map(([day, hours]) => {
        return hours.isOpen
          ? `${day}: ${hours.start} - ${hours.end}`
          : `${day}: Closed`;
      });
    onChange(formattedHours);
  };

  const copyHours = (fromDay) => {
    const sourceHours = businessHours[fromDay];
    const updatedHours = { ...businessHours };
    
    Object.keys(updatedHours)
      .filter(day => !day.includes('-'))
      .forEach(day => {
        if (day !== fromDay) {
          updatedHours[day] = { ...sourceHours };
        }
      });
    
    setBusinessHours(updatedHours);
    const formattedHours = Object.entries(updatedHours)
      .filter(([day]) => !day.includes('-'))
      .map(([day, hours]) => {
        return hours.isOpen
          ? `${day}: ${hours.start} - ${hours.end}`
          : `${day}: Closed`;
      });
    onChange(formattedHours);
  };

  return (
    <div className="space-y-4">
      {Object.entries(businessHours)
        .filter(([day]) => !day.includes('-'))
        .map(([day, hours]) => (
          <div key={day} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-28">
              <span className="font-medium">{day}</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={hours.isOpen}
                  onChange={(e) => handleHourChange(day, 'isOpen', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer 
                  peer-focus:ring-4 peer-focus:ring-blue-300 
                  peer-checked:after:translate-x-full peer-checked:after:border-white 
                  after:content-[''] after:absolute after:top-0.5 after:left-[2px] 
                  after:bg-white after:border-gray-300 after:border after:rounded-full 
                  after:h-5 after:w-5 after:transition-all 
                  peer-checked:bg-blue-600">
                </div>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {hours.isOpen ? 'Open' : 'Closed'}
                </span>
              </label>
            </div>

            {hours.isOpen && (
              <>
                <div className="flex items-center space-x-2">
                  <select
                    value={hours.start}
                    onChange={(e) => handleHourChange(day, 'start', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {timeOptions.map(time => (
                      <option key={`start-${time}`} value={time}>{time}</option>
                    ))}
                  </select>
                  <span>to</span>
                  <select
                    value={hours.end}
                    onChange={(e) => handleHourChange(day, 'end', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {timeOptions.map(time => (
                      <option key={`end-${time}`} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                {day === 'Monday' && (
                  <button
                    onClick={() => copyHours(day)}
                    className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200"
                  >
                    Copy to all days
                  </button>
                )}
              </>
            )}
          </div>
        ))}
    </div>
  );
};

export default BusinessHours; 